// src/services/cacheService.js
/**
 * High-level cache service that coordinates IndexedDB caching
 * Handles full sync, incremental sync, and cache management
 */

import indexedDBService from './indexedDBService';
import { fetchWorkspaces, fetchDocuments } from '../api/workspacesApi';

class CacheService {
  constructor() {
    this.syncInProgress = false;
    this.syncProgress = { current: 0, total: 0, phase: '' };
    this.listeners = [];
  }

  /**
   * Subscribe to sync progress updates
   */
  onProgress(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all listeners of progress
   */
  notifyProgress(current, total, phase) {
    this.syncProgress = { current, total, phase };
    this.listeners.forEach(callback => callback(this.syncProgress));
  }

  /**
   * Check if device is trusted
   */
  isTrustedDevice() {
    return localStorage.getItem('device_trusted') === 'true';
  }

  /**
   * Mark device as trusted
   */
  setDeviceTrusted(trusted) {
    if (trusted) {
      localStorage.setItem('device_trusted', 'true');
    } else {
      localStorage.removeItem('device_trusted');
    }
  }

  /**
   * Full cache sync - download everything
   */
/**
 * Full cache sync - download everything
 */
async fullSync(userId) {
  if (this.syncInProgress) {
    console.log('⏭️ Sync already in progress');
    return;
  }

  this.syncInProgress = true;
  console.log('📥 Starting full cache sync for user:', userId);

  try {
    // Phase 1: Fetch all workspaces
    this.notifyProgress(0, 100, 'Fetching workspaces...');
    const workspaces = await fetchWorkspaces();
    
    console.log(`📦 Caching ${workspaces.length} workspaces`);
    
    // Save workspaces
    for (let i = 0; i < workspaces.length; i++) {
      await indexedDBService.saveWorkspace(workspaces[i]);
      this.notifyProgress(i + 1, workspaces.length, 'Caching workspaces...');
    }

    // Phase 2: Fetch documents for each workspace
    let totalDocuments = 0;
    let cachedDocuments = 0;
    
    this.notifyProgress(0, workspaces.length, 'Fetching documents...');
    
    for (let i = 0; i < workspaces.length; i++) {
      const workspace = workspaces[i];
      try {
        const response = await fetchDocuments(workspace.public_id);
        const documents = response.documents || response || [];
        
        totalDocuments += documents.length;
        
        // Save each document
        for (const doc of documents) {
          await indexedDBService.saveDocument(doc);
          cachedDocuments++;
        }
        
        this.notifyProgress(
          i + 1, 
          workspaces.length, 
          `Caching documents (${cachedDocuments} total)...`
        );
      } catch (error) {
        console.error(`Error fetching documents for workspace ${workspace.public_id}:`, error);
      }
    }

    // Phase 3: Fetch notes for each workspace
      let totalNotes = 0;
      let cachedNotes = 0;

      this.notifyProgress(0, workspaces.length, 'Fetching notes...');

      for (let i = 0; i < workspaces.length; i++) {
        const workspace = workspaces[i];
        try {
          const notesResponse = await fetch(`/api/workspaces/${workspace.public_id}/notes`, {
            credentials: 'include'
          });
          
          if (notesResponse.ok) {
            const notes = await notesResponse.json();
            
            totalNotes += notes.length;
            
            // Save each note WITH workspace_public_id added
            for (const note of notes) {
              // ✅ ADD workspace_public_id to the note
              const enrichedNote = {
                ...note,
                workspace_public_id: workspace.public_id  // Add UUID for searching
              };
              await indexedDBService.saveNote(enrichedNote);
              cachedNotes++;
            }
          }
          
          this.notifyProgress(
            i + 1, 
            workspaces.length, 
            `Caching notes (${cachedNotes} total)...`
          );
        } catch (error) {
          console.error(`Error fetching notes for workspace ${workspace.public_id}:`, error);
        }
      }

    // Save sync metadata
    await indexedDBService.setLastSyncTime(new Date().toISOString());
    
    this.notifyProgress(100, 100, 'Cache complete!');
    console.log(`✅ Full sync complete: ${workspaces.length} workspaces, ${totalDocuments} documents, ${totalNotes} notes`);
    
    return {
      workspaces: workspaces.length,
      documents: totalDocuments,
      notes: totalNotes
    };
    
  } catch (error) {
    console.error('❌ Full sync failed:', error);
    throw error;
  } finally {
    this.syncInProgress = false;
  }
}

  /**
   * Incremental sync - only fetch changes since last sync
   */
  async incrementalSync(userId) {
    if (this.syncInProgress) {
      console.log('⏭️ Sync already in progress');
      return;
    }

    const lastSyncTime = await indexedDBService.getLastSyncTime();
    
    if (!lastSyncTime) {
      console.log('No last sync time, performing full sync');
      return this.fullSync(userId);
    }

    this.syncInProgress = true;
    console.log('🔄 Starting incremental sync since:', lastSyncTime);

    try {
      this.notifyProgress(0, 100, 'Checking for updates...');
      
      // Fetch all workspaces (lightweight query)
      const serverWorkspaces = await fetchWorkspaces();
      const cachedWorkspaces = await indexedDBService.getAllWorkspaces();
      
      // Find new or modified workspaces
      const updatedWorkspaces = serverWorkspaces.filter(serverWs => {
        const cached = cachedWorkspaces.find(c => c.public_id === serverWs.public_id);
        if (!cached) return true; // New workspace
        return new Date(serverWs.modified_date) > new Date(cached.modified_date);
      });

      // Find DELETED workspaces
            const serverIds = new Set(serverWorkspaces.map(w => w.public_id));
            const deletedWorkspaces = cachedWorkspaces.filter(cached => !serverIds.has(cached.public_id));

            for (const deletedWs of deletedWorkspaces) {
              console.log(`🗑️ Removing deleted workspace: ${deletedWs.name}`);
              await indexedDBService.deleteWorkspace(deletedWs.public_id);
              
              // Delete its documents
              const docs = await indexedDBService.getDocumentsByWorkspace(deletedWs.public_id);
              for (const doc of docs) {
                await indexedDBService.deleteDocument(doc.public_id);
              }
              
              // Delete its notes
              const notes = await indexedDBService.getNotesByWorkspace(deletedWs.public_id);
              for (const note of notes) {
                await indexedDBService.deleteNote(note.id);
              }
            }

      if (updatedWorkspaces.length === 0) {
        console.log('✅ Everything up to date');
        this.notifyProgress(100, 100, 'Up to date');
        await indexedDBService.setLastSyncTime(new Date().toISOString());
        return { workspaces: 0, documents: 0 };
      }

      console.log(`📦 Syncing ${updatedWorkspaces.length} updated workspaces`);
      
      let totalDocuments = 0;
      
      for (let i = 0; i < updatedWorkspaces.length; i++) {
        const workspace = updatedWorkspaces[i];
        
        // Save workspace
        await indexedDBService.saveWorkspace(workspace);
        
        // Fetch and save documents
        try {
          const response = await fetchDocuments(workspace.public_id);
          const documents = response.documents || response || [];
          
          for (const doc of documents) {
            await indexedDBService.saveDocument(doc);
            totalDocuments++;
          }
          
          this.notifyProgress(
            i + 1,
            updatedWorkspaces.length,
            `Syncing updates (${totalDocuments} documents)...`
          );
        } catch (error) {
          console.error(`Error syncing workspace ${workspace.public_id}:`, error);
        }
      }

      await indexedDBService.setLastSyncTime(new Date().toISOString());
      
      this.notifyProgress(100, 100, 'Sync complete');
      console.log(`✅ Incremental sync complete: ${updatedWorkspaces.length} workspaces, ${totalDocuments} documents`);
      
      return {
        workspaces: updatedWorkspaces.length,
        documents: totalDocuments
      };
      
    } catch (error) {
      console.error('❌ Incremental sync failed:', error);
      throw error;
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Clear all cached data (on logout for untrusted devices)
   */
  async clearCache() {
    console.log('🗑️ Clearing all cached data');
    await indexedDBService.clearAll();
    this.setDeviceTrusted(false);
  }

  /**
   * Search cached data
   */
  async searchLocal(query) {
    if (!this.isTrustedDevice()) {
      throw new Error('Search not available - device not trusted');
    }
    
    return indexedDBService.search(query);
  }

  /**
   * Get sync statistics
   */
  async getSyncStats() {
    const workspaces = await indexedDBService.getAllWorkspaces();
    const documents = await indexedDBService.getAllDocuments();
    const notes = await indexedDBService.getAllNotes();
    const lastSync = await indexedDBService.getLastSyncTime();

    return {
      workspaces: workspaces.length,
      documents: documents.length,
      notes: notes.length,
      lastSync,
      isTrusted: this.isTrustedDevice()
    };
  }
}

// Export singleton instance
const cacheService = new CacheService();
export default cacheService;